import './Message.sass';

import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import copyToClipboard from 'copy-text-to-clipboard';
import { useDispatch, useSelector } from 'react-redux';

import { animated } from '@react-spring/web';

import { MessageProps } from '$core/Chat/Message/Message.interface';
import { REPLY_SWIPE_THRESHOLD, useReplySwipe } from '$core/Chat/useReplySwipe';
import { getReplyPreview, scrollToChatMessage } from '$core/Chat/replyPreview';
import {
  useCurrentUserInfo,
  useDesktop,
  useImActions,
  useTranslator,
  useUserInfo,
} from '$hooks';
import { classNames, format, formatCurrency, haptic } from '$utils';
import { Attachments, Currency, Linkify } from '$shared/components';
import {
  IcBanOff28,
  IcClock28,
  IcCopy28,
  IcError28,
  IcReport24,
  IcShare24,
  IcStatusRead16,
  IcStatusSent16,
} from '$assets';
import { imActions, imAsPeerIdSelector, imSelector } from '$store/im';
import { ActionBottomSheet, AnimatedEmoji, Button } from '$uikit';
import { toastActions } from '$store/toast';
import { showModal } from '$navigation/helper';
import { mainSelector } from '$store/main';
import { ReportReason } from '$shared/constants/reportReason';
import router from '$navigation/router';
import { CHAT } from '$shared/constants/pages';
import {
  AttachType,
  CurrencyEnum,
  MessageTelegramStatus,
  SubscriptionType,
} from '$store/models';
import { balancePayActions } from '$store/balancePay';
import { BalancePayAction } from '$store/balancePay/interface';
import { ActionBottomSheetOption } from '$uikit/ActionBottomSheet/ActionBottomSheet.interface';
import { LargeEmoji } from '$assets/mapping';
import { DiscountButton } from '$uikit/DiscountButton/DiscountButton';

enum MessageActionsGroups {
  REPORT = 'report',
}

const BOUNCE_SHEET_DELAY = 120;
const LONG_PRESS_DELAY = 500;

export const Message: FC<MessageProps> = (props) => {
  const {
    message,
    peerId,
    isGroupingBefore,
    isGroupingAfter,
    isOutOfChat,
    isHighlighted,
  } = props;

  const [isActionShow, setActionsShown] = useState(false);
  const [isBouncing, setIsBouncing] = useState(false);
  const time = useMemo(
    () => format(message.createdAt * 1000, 'HH:mm'),
    [message.createdAt],
  );
  const curUser = useCurrentUserInfo();
  const isDesktop = useDesktop();
  const { decryptKeys, mVerified } = useSelector(mainSelector);
  const { tonRate } = useSelector(imSelector);
  const asPeerId = useSelector(imAsPeerIdSelector);
  const isInbox = asPeerId ? message.fromId !== asPeerId : message.fromId !== curUser.id;
  const hasAttachments = message.attachments && message.attachments.length > 0;
  const dispatch = useDispatch();
  const im = useImActions();
  const t = useTranslator();
  const bundle = useMemo(() => {
    return message.attachments?.find((a) => a.bundle)?.bundle;
  }, [message.attachments]);
  const battlePrize = useMemo(() => {
    return message.attachments?.find((a) => a.battlePrize)?.battlePrize;
  }, [message.attachments]);
  const isPaidMessage =
    (!!message.price && message.price > 0) || !!bundle || !!battlePrize;
  const isBlur = isPaidMessage && message.isHidden && isInbox;
  const isIncomeMessageNotPaid = message.isHidden && isInbox;
  const isPaidMessageButtonEnabled =
    isPaidMessage && (isIncomeMessageNotPaid || !isInbox);
  const peer = useUserInfo(peerId);

  const isSecondary =
    !isInbox && (message.isWelcome || message.isBroadcast || bundle || battlePrize);

  const canReply =
    !isOutOfChat && !isBlur && !message.isFailed && !message.isSending && message.id > 0;

  function renderSendIndicator() {
    if (message.isSending) {
      return (
        <svg
          className="Message__sending_clock"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="8"
            cy="8"
            r="5.5"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            className="Message__sending_clock__minute"
            d="M8 8V4.5"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            className="Message__sending_clock__hour"
            d="M8 8L10 9.5"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    } else if (message.isFailed) {
      return null;
    } else if (message.isUnread) {
      return <IcStatusSent16 />;
    } else {
      return <IcStatusRead16 />;
    }
  }

  function renderTelegramStatus() {
    if (message.telegramStatus === MessageTelegramStatus.Sending) {
      return t('message_tg_status_sending');
    } else if (message.telegramStatus === MessageTelegramStatus.Received) {
      return t('message_tg_status_received');
    } else if (message.telegramStatus === MessageTelegramStatus.Failed) {
      return t('message_tg_status_failed');
    } else {
      return null;
    }
  }

  function renderPaidMessageMetaLabel() {
    if (tonRate === null || !message.price) {
      return null;
    }

    if (message.isHidden && message.currency === CurrencyEnum.TON) {
      return (
        <span>{`~$${(message.price * tonRate).toFixed(2)}, ${t(
          'message_paid_pending',
        )}, `}</span>
      );
    }

    return (
      <span>{`${formatCurrency(message.price, message.currency || CurrencyEnum.TON)}, ${t(
        'message_meta_paid',
      )}, `}</span>
    );
  }

  const messageMeta = (
    <div className="Message__meta">
      {peer?.isTelegramUser && !message.isInbox && (
        <div className="Message__meta__telegram_status">{renderTelegramStatus()}</div>
      )}
      {isPaidMessage && renderPaidMessageMetaLabel()}
      {bundle && (
        <div className="Message__battle">
          <Currency amount={bundle.price} currency={bundle.currency} />, Bundle,{' '}
        </div>
      )}
      {battlePrize && <div className="Message__bundle">Battle, </div>}
      <div className="Message__time">{time}</div>
      {!message.isInbox && !bundle && !battlePrize && (
        <div className="Message__send_indicator">{renderSendIndicator()}</div>
      )}
    </div>
  );

  const text = useMemo(() => {
    return message.text.trim().replace(/\n{3,}/g, '\n\n');
  }, []);

  const attachWidth = useMemo(() => {
    return Math.min(600, window.innerWidth) - 84 - 16 - 12 * 2; // 12 is inner bubble padding
  }, []);

  const actions = useMemo(() => {
    const items: ActionBottomSheetOption[] = [];

    if (message.isFailed) {
      const failedItems: ActionBottomSheetOption[] = [
        { label: t('send_button'), value: 'retry' },
        { label: t('message_delete'), value: 'delete', mode: 'alert' },
      ];
      return failedItems;
    }

    if (isOutOfChat) {
      return [{ label: t('message_go_to_chat'), value: 'goToChat' }];
    }

    if (canReply) {
      items.push({
        label: t('message_reply', { defaultValue: 'Reply' }),
        value: 'reply',
      });
    }

    if (message.text.trim().length > 0) {
      items.push({ label: t('message_copy_text'), value: 'copy', icon: <IcCopy28 /> });
    }

    if (message.isInbox) {
      items.push({
        label: t('message_report'),
        value: 'report',
        icon: <IcReport24 />,
        options: [
          {
            label: t('post_action_report_spam'),
            value: ReportReason.SPAM,
            groupId: MessageActionsGroups.REPORT,
          },
          {
            label: t('post_action_report_offensive'),
            value: ReportReason.OFFENSIVE,
            groupId: MessageActionsGroups.REPORT,
          },
          {
            label: t('post_action_report_false_info'),
            value: ReportReason.FALSE_INFO,
            groupId: MessageActionsGroups.REPORT,
          },
        ],
      });
    } else {
      if (!message.price || (message.price && message.isHidden)) {
        items.push({ label: t('message_delete'), value: 'delete', mode: 'alert' });
      }
    }

    if (message.telegramStatus === MessageTelegramStatus.Failed) {
      items.push({ label: t('message_tg_retry'), value: 'telegram_retry' });
    }

    return items;
  }, [message, t, canReply]);

  const longPressTimer = useRef<number | null>(null);
  const isLongPress = useRef(false);

  const showActions = useCallback(() => {
    if (actions.length === 0) {
      return;
    }

    setIsBouncing(true);
    setTimeout(() => {
      haptic('light');
      setActionsShown(true);
    }, BOUNCE_SHEET_DELAY);
  }, [actions.length]);

  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current !== null) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handlePressStart = useCallback(
    ({ target }) => {
      if (isBlur || !target.closest('.Collage__item')) {
        return;
      }

      isLongPress.current = false;
      longPressTimer.current = window.setTimeout(() => {
        longPressTimer.current = null;
        isLongPress.current = true;
        showActions();
      }, LONG_PRESS_DELAY);
    },
    [isBlur, showActions],
  );

  const {
    bindSwipe,
    x,
    didSwipe,
    reply: handleReply,
  } = useReplySwipe(peerId, message, !isDesktop && canReply, cancelLongPress);

  const handleClick = useCallback(
    ({ target }) => {
      if (isLongPress.current) {
        isLongPress.current = false;
        return;
      }

      if (didSwipe.current) {
        return;
      }

      if (isBlur) {
        return;
      }

      if (target.closest('.TipsAttach__post_btn')) {
        return;
      } else if (target.closest('.Collage__item.photo')) {
        const photoId = +target.closest('.Collage__item').dataset.id;
        const partId = +target.closest('.Collage__item').dataset.part;
        showModal(
          [
            'photos',
            isOutOfChat ? `paid${curUser.id}` : `history${peerId}`,
            photoId,
            message.id,
            partId,
          ]
            .filter(Boolean)
            .join('+'),
        );
        return;
      } else if (target.closest('.Collage__item.video')) {
        const videoId = +target.closest('.Collage__item').dataset.id;
        const partId = +target.closest('.Collage__item').dataset.part;
        showModal(
          [
            'photos',
            isOutOfChat ? `paid${curUser.id}` : `history${peerId}`,
            -videoId,
            message.id,
            partId,
          ]
            .filter(Boolean)
            .join('+'),
        );
        return;
      }

      showActions();
    },
    [message, isBlur, peerId, isOutOfChat, curUser.id, showActions],
  );

  const handleAction = useCallback(
    (value) => {
      setActionsShown(false);

      if (isBlur) {
        return;
      }

      if (value === 'reply') {
        handleReply();
      } else if (value === 'copy') {
        copyToClipboard(message.text);
        dispatch(toastActions.setToastSuccess(t('copied')));
      } else if (value === 'retry') {
        im.retrySend(peerId, message);
      } else if (value === 'delete') {
        if (message.isFailed) {
          im.deleteUnsentMessage(message.id, peerId);
        } else {
          im.deleteMessage(message.id, peerId);
        }
      } else if (Object.values(ReportReason).includes(value)) {
        dispatch(
          imActions.reportMessage({
            messageId: message.id,
            reason: value,
          }),
        );
      } else if (value === 'goToChat') {
        router.navigate(CHAT, { peerId: peerId });
      } else if (value === 'telegram_retry') {
        dispatch(
          imActions.telegramRetry({
            messageId: message.id,
            peerId,
          }),
        );
      }
    },
    [dispatch, peerId, message, t, isBlur, handleReply],
  );

  const handleBuy = useCallback(() => {
    if (message.currency === CurrencyEnum.EUR) {
      dispatch(
        balancePayActions.open({
          amount: message.price!,
          action: BalancePayAction.MESSAGE,
          objectId: peerId,
          extraId: message.id,
        }),
      );
    } else {
      dispatch(imActions.buyMessage({ peerId, message }));
    }
  }, [dispatch, message, peer]);

  const replyAuthor = useUserInfo(message.replyTo?.fromId ?? 0);
  const replyAuthorName = useMemo(() => {
    if (!message.replyTo) {
      return '';
    }

    const author = replyAuthor ?? (message.replyTo.fromId === peerId ? peer : curUser);
    return [author?.firstName, author?.lastName].filter(Boolean).join(' ');
  }, [message.replyTo, peerId, peer, curUser, replyAuthor]);

  const replyPreview = useMemo(() => {
    return message.replyTo ? getReplyPreview(message.replyTo, t) : null;
  }, [message.replyTo, t]);

  const handleReplyClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!message.replyTo || message.replyTo.isDeleted) {
        return;
      }

      scrollToChatMessage(message.replyTo.id);
    },
    [message.replyTo],
  );

  function renderPaidMessageButton() {
    if (!isInbox) {
      if (message.isHidden) {
        return (
          <div className="Message__pay_status">
            <IcClock28 color="var(--accent-color-orange)" />
          </div>
        );
      }

      return (
        <div className="Message__pay_status">
          <IcBanOff28 color="var(--accent-color-green)" />
        </div>
      );
    }

    if (!message.isHidden) {
      return (
        <div className="Message__pay_status">
          <IcBanOff28 color="var(--accent-color-green)" />
        </div>
      );
    }
  }

  function renderPaidMessageLabel() {
    if (isInbox) {
      return <div className="Message__pay_caption">{t('message_paid_label')}</div>;
    }

    if (message.isHidden) {
      return (
        <div className="Message__pay_caption pending">{t('message_paid_pending')}</div>
      );
    }

    return <div className="Message__pay_caption paid">{t('message_paid')}</div>;
  }

  return (
    <>
      <div
        id={`${message.id}`}
        className={classNames({
          Message: true,
          inbox: isInbox,
          outbox: !isInbox,
          withAttach: hasAttachments,
          isGroupingBefore,
          isGroupingAfter,
          isHidden: message.isHidden && isInbox,
          highlighted: !!isHighlighted,
        })}
      >
        {message.isFailed && (
          <div className="Message__failed" onClick={handleClick}>
            <IcError28 />
          </div>
        )}
        {!isDesktop && canReply && (
          <animated.div
            className="Message__reply_swipe_icon"
            style={{ opacity: x.to((v) => Math.min(1, -v / REPLY_SWIPE_THRESHOLD)) }}
          >
            <IcShare24 />
          </animated.div>
        )}
        <animated.div
          className={classNames('Message__in', {
            secondary: isSecondary,
            'is-bouncing': isBouncing,
          })}
          style={{ x }}
          {...bindSwipe()}
          onClick={handleClick}
          onTouchStart={handlePressStart}
          onTouchMove={cancelLongPress}
          onTouchEnd={cancelLongPress}
          onMouseDown={handlePressStart}
          onMouseUp={cancelLongPress}
          onMouseLeave={cancelLongPress}
          onContextMenu={(e) => {
            if ((e.target as HTMLElement).closest('.Collage__item')) {
              e.preventDefault();
            }
          }}
          onAnimationEnd={() => setIsBouncing(false)}
        >
          <div
            className={classNames('Message__cont', {
              _PaidMessage: isPaidMessage,
            })}
          >
            {message.replyTo && replyPreview && (
              <div className="Message__reply" onClick={handleReplyClick}>
                {replyPreview.imageUrl && (
                  <div
                    className="Message__reply__img"
                    style={{ backgroundImage: `url(${replyPreview.imageUrl})` }}
                  />
                )}
                <div className="Message__reply__body">
                  <div className="Message__reply__name">{replyAuthorName}</div>
                  <div className="Message__reply__text">{replyPreview.text}</div>
                </div>
              </div>
            )}
            {isPaidMessageButtonEnabled &&
              !message.attachments?.some(
                (a) => a.type === AttachType.ROULETTE_LOT || a.type === AttachType.BATTLE,
              ) &&
              (isInbox && message.isHidden ? (
                // <Button
                //   size="small"
                //   className="Message__pay_button"
                //   onClick={handleBuy}
                // >{`${t('message_unlock_button')} ${formatCurrency(
                //   message.price ?? 0,
                //   message.currency ?? CurrencyEnum.TON,
                // )}`}</Button>
                <DiscountButton
                  size="small"
                  className="Message__pay_button"
                  price={message.price ?? 0}
                  priceWithoutDiscount={message.priceWithoutDiscount}
                  currency={message.currency ?? CurrencyEnum.TON}
                  buyText="message_unlock_button"
                  saveText="message_unlock_save_button"
                  onClick={handleBuy}
                />
              ) : (
                !bundle &&
                !battlePrize && (
                  <div className="Message__pay_wrap">
                    <div className="Message__pay_info">
                      <div className="Message__pay_price">
                        {formatCurrency(
                          message.price ?? 0,
                          message.currency ?? CurrencyEnum.TON,
                        )}
                      </div>
                      <div className="Message__pay_caption">
                        {renderPaidMessageLabel()}
                      </div>
                    </div>
                    <div>{renderPaidMessageButton()}</div>
                  </div>
                )
              ))}
            {text.length > 0 && (
              <div
                style={{
                  marginTop:
                    isPaidMessage &&
                    (isIncomeMessageNotPaid || (!isInbox && !message.isHidden))
                      ? '12px'
                      : undefined,
                }}
                className={classNames({
                  Message__text: true,
                  //blur: isBlur,
                })}
              >
                {LargeEmoji[text] ? (
                  <AnimatedEmoji emoji={text} />
                ) : (
                  <Linkify>{text}</Linkify>
                )}
                {!hasAttachments && messageMeta}
              </div>
            )}
            <div
              className="Message__attachments"
              onClick={() => (isIncomeMessageNotPaid ? handleBuy() : null)}
            >
              <Attachments
                ratio={1.6}
                price={message.price}
                attachments={message.attachments}
                photosWidth={attachWidth}
                parentObject={`message_${message.id}`}
                decryptKey={
                  message.decryptKey || decryptKeys[-SubscriptionType.Media] || ''
                }
                isBlocked={message.isHidden && isInbox}
                forceBlur={
                  mVerified &&
                  isInbox &&
                  !message.attachments?.some(
                    (a) =>
                      a.type === AttachType.ROULETTE_LOT || a.type === AttachType.BATTLE,
                  )
                }
                canUnblur
              />
            </div>
            {messageMeta}
          </div>
        </animated.div>
      </div>
      <ActionBottomSheet
        title={t('message_actions_title')}
        isOpened={isActionShow}
        options={actions}
        onChange={handleAction}
        onClose={() => setActionsShown(false)}
      />
    </>
  );
};
